import { hasAtLeastOrgRole, hasAtLeastProjectRole, orgRoleRank, projectRoleRank, } from './enums.js';
/**
 * The RBAC matrix, as data. Every API route, MCP tool and UI affordance asks `canOrg`/`canProject`
 * with a named permission instead of comparing ranks inline, so "who may do X" is answered in one
 * place and the README matrix can be read straight off these two tables.
 *
 * Each entry is the minimum role that holds the permission (roles are strictly ranked, so a
 * higher role satisfies a lower requirement). Permissions whose answer also depends on the target
 * — "own deliverable", "your own task", "a role below yours" — are the `*.any` entries here plus
 * the helpers further down, which the caller combines with the facts it has about the target.
 */
export const ORG_PERMISSIONS = {
    /** See the org's name and the projects you belong to. */
    'org.view': 'guest',
    /** See the org's member directory and read its dev-preference defaults. */
    'org.members.view': 'member',
    'org.project.create': 'member',
    /** See and open every project in the org, not just the ones you're a member of. */
    'org.projects.viewAll': 'admin',
    /** Rename the org, edit dev-preference defaults. */
    'org.update': 'admin',
    /** Invite, add and remove `member`/`guest`. Granting anything higher is checked by canGrantOrgRole. */
    'org.members.manage': 'admin',
    /** Promote to admin, demote or remove an admin. */
    'org.admins.manage': 'owner',
    /** Grant or transfer `owner`. */
    'org.owner.grant': 'owner',
    /** The full audit log, including actor IPs. */
    'org.audit.read': 'admin',
    /** The Team activity page: everyone's status history and time-in-status stats. Live status
     * dots next to names are visible to everyone; only the history is restricted. */
    'org.teamActivity.view': 'admin',
    'org.delete': 'owner',
};
export const PROJECT_PERMISSIONS = {
    'project.read': 'viewer',
    'project.export': 'viewer',
    /** The project's activity feed. Viewers are deliberately excluded. */
    'project.activity.read': 'member',
    /** Viewers may also comment when the project's `viewerCanComment` option is on — see canComment. */
    'task.comment': 'member',
    'task.create': 'member',
    'task.edit': 'member',
    /** Claim an unassigned task or release your own. */
    'task.claim': 'member',
    /** Move any task to done/canceled. A member may only close a task assigned to them. */
    'task.close.any': 'lead',
    'task.assign.any': 'lead',
    'task.delete': 'lead',
    'issue.raise': 'member',
    /** Read and post in the project's discussion — react, reply, pin, @mention. Viewers neither
     * see nor post in it. Editing and deleting a message is its author's alone (no `.any`). */
    'discussion.participate': 'member',
    /** Resolve any blocker. A member may resolve one they raised or one on a task assigned to them. */
    'issue.resolve.any': 'lead',
    /** Save PRD/TDD drafts and run LLM generation (extract, questionnaire, generate). */
    'document.draft': 'member',
    /** Accept a PRD/TDD version, or commit a generated task list — both record a human sign-off. */
    'document.accept': 'lead',
    'regeneration.start': 'member',
    'regeneration.decide': 'lead',
    /** Training projects: upload sources, run every generation step, confirm sessions and
     * outlines, edit slides and restore deck versions. Members and viewers only view and export. */
    'training.manage': 'lead',
    'deliverable.write': 'member',
    /** Delete any deliverable. A member may delete only one they added. */
    'deliverable.delete.any': 'lead',
    /** Name, dates, type-independent settings, the viewer-comment option. */
    'project.update': 'lead',
    /** Add/remove `member`/`viewer`. Anything higher is checked by canGrantProjectRole. */
    'project.members.manage': 'lead',
    /** The full project audit log, including actor IPs. */
    'project.audit.read': 'lead',
    /** Add/remove `lead`, change `leadUid`. */
    'project.leads.manage': 'owner',
    'project.owner.grant': 'owner',
    'project.archive': 'owner',
    'project.delete': 'owner',
};
export function canOrg(role, permission) {
    return !!role && hasAtLeastOrgRole(role, ORG_PERMISSIONS[permission]);
}
export function canProject(role, permission) {
    return !!role && hasAtLeastProjectRole(role, PROJECT_PERMISSIONS[permission]);
}
/** The custom claim that marks an app-wide administrator — the only people who may manage the
 * global domain allowlist from the web. Set with `pulse user:platform-admin`. Owning an org
 * confers nothing app-wide: anyone can create an org and become its owner. */
export const PLATFORM_ADMIN_CLAIM = 'platformAdmin';
/** The highest project role an org guest may hold, whatever they're granted. */
export const GUEST_MAX_PROJECT_ROLE = 'member';
/** The project role an org role implies on every project in the org, with no membership doc. */
export function implicitProjectRole(orgRole) {
    if (orgRole === 'owner')
        return 'owner';
    if (orgRole === 'admin')
        return 'lead';
    return null;
}
function maxProjectRole(a, b) {
    if (!a)
        return b;
    if (!b)
        return a;
    return projectRoleRank[a] >= projectRoleRank[b] ? a : b;
}
/**
 * The role a user actually holds on a project: the higher of their explicit membership and what
 * their org role implies, with guests capped at GUEST_MAX_PROJECT_ROLE. Someone who is no longer
 * in the project's org holds nothing, even if a stale membership doc survives.
 */
export function effectiveProjectRole(orgRole, explicitRole) {
    if (!orgRole)
        return null;
    const explicit = explicitRole &&
        orgRole === 'guest' &&
        !hasAtLeastProjectRole(GUEST_MAX_PROJECT_ROLE, explicitRole)
        ? GUEST_MAX_PROJECT_ROLE
        : (explicitRole ?? null);
    return maxProjectRole(explicit, implicitProjectRole(orgRole));
}
/** The ceiling applied to an AI agent acting through the MCP connector: whatever its user holds,
 * the agent never gets lead/owner powers — those need a human in the UI. */
export const AGENT_MAX_PROJECT_ROLE = 'member';
export function agentProjectRole(role) {
    return hasAtLeastProjectRole(role, AGENT_MAX_PROJECT_ROLE) ? AGENT_MAX_PROJECT_ROLE : role;
}
/**
 * Whether `actor` may move someone from `from` (null = not yet a member) to `to` (null = remove
 * them). You can only grant roles strictly below your own and only touch people strictly below
 * you — except an owner, who may do anything, including grant `owner` (which is how ownership is
 * transferred). The last-owner guard is separate (it needs a count), see the API routes.
 */
export function canGrantOrgRole(actor, from, to) {
    if (!actor)
        return false;
    if (actor === 'owner')
        return true;
    if (!canOrg(actor, 'org.members.manage'))
        return false;
    const below = (r) => r === null || orgRoleRank[r] < orgRoleRank[actor];
    return below(from) && below(to);
}
/** Project-scope twin of canGrantOrgRole. */
export function canGrantProjectRole(actor, from, to) {
    if (!actor)
        return false;
    if (actor === 'owner')
        return true;
    if (!canProject(actor, 'project.members.manage'))
        return false;
    const below = (r) => r === null || projectRoleRank[r] < projectRoleRank[actor];
    return below(from) && below(to);
}
/** Comment on a task: members and up, or a viewer on a project that has opted in. */
export function canComment(role, project) {
    if (canProject(role, 'task.comment'))
        return true;
    return role === 'viewer' && project.viewerCanComment === true;
}
/** Move a task to done/canceled. */
export function canCloseTask(role, uid, task) {
    if (canProject(role, 'task.close.any'))
        return true;
    return canProject(role, 'task.edit') && !!task.assigneeUid && task.assigneeUid === uid;
}
/** The statuses that count as closing a task, for canCloseTask. */
export const CLOSING_TASK_STATUSES = ['done', 'canceled'];
/** Resolve a blocker. */
export function canResolveIssue(role, uid, issue, task) {
    if (canProject(role, 'issue.resolve.any'))
        return true;
    if (!canProject(role, 'issue.raise'))
        return false;
    return issue.raisedByUid === uid || (!!task.assigneeUid && task.assigneeUid === uid);
}
/** Delete a deliverable. */
export function canDeleteDeliverable(role, uid, deliverable) {
    if (canProject(role, 'deliverable.delete.any'))
        return true;
    return canProject(role, 'deliverable.write') && deliverable.createdByUid === uid;
}
//# sourceMappingURL=permissions.js.map